import { useState, useEffect, useContext, Suspense, lazy, useRef } from 'react';
import { Button } from 'react-bootstrap';

import ChatSession from '../components/chat/ChatSession';

import MainHeader from '../components/home/MainHeader';
import { SamplePrompts } from '../components/home/SamplePrompts';
import ChatInput from '../components/chat/ChatInput';
const PrivateChat = lazy(() => import('../components/chat/PrivateChat'));

import { PromptContext } from '../context/PromptContext';
import { LoadingContext } from '../context/LoadingContext';
import notify from '../services/notify';
import { PROFILE_INCOMPLETE_TOAST_ID, SERVICE_NOW_LINK } from '../config/settings';

import ArrowDownwardsIcon from '../assets/icon-arrow-downwards.svg?react';
import { SettingContext } from '@/context/SettingContext';
import { AuthContext } from '@/context/AuthContext';
import AppAgentWelcome from '@/components/app-builder/AppAgentWelcome';
import { useNavigate } from 'react-router-dom';
import { getAiApp, getAppIcon } from '../services/aiApps';
import useElysiaStore from '@/app/elysia-store';
import BlockModal from '@/components/home/blockModal';
let lastScrollTop = 0;

const Home = ({ contentRef }) => {
  const uploadedIcon = useElysiaStore((state) => state.uploadedIcon);
  const aiAppId = useElysiaStore((state) => state.aiAppId);
  const isAppTerminated = useElysiaStore((state) => state.isAppTerminated);
  const isRestrictedChatSessionStatus = useElysiaStore(
    (state) => state.isRestrictedChatSessionStatus
  );
  const localAutoScrollEnabledKey =
    useElysiaStore((state) => state.localAutoScrollEnabledKey) || 'autoScrollEnabled';
  const isChatActive = useElysiaStore((state) => state.isChatActive);
  const isPrivateChat = useElysiaStore((state) => state.isPrivateChat);
  const showScrollIcon = useElysiaStore((state) => state.showScrollIcon);
  const dynamicStreamCount = useElysiaStore((state) => state.dynamicStreamCount);
  const chatSessionId = useElysiaStore((state) => state.chatSessionId);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const setName = useElysiaStore.getState().setName;
  const setDescription = useElysiaStore.getState().setDescription;
  const setUploadedIcon = useElysiaStore.getState().setUploadedIcon;
  const setGreetings = useElysiaStore.getState().setGreetings;
  const setAppCreator = useElysiaStore.getState().setAppCreator;
  const setStarterPrompts = useElysiaStore.getState().setStarterPrompts;
  const setAiAppId = useElysiaStore.getState().setAiAppId;
  const setPromptToolsConfig = useElysiaStore.getState().setPromptToolsConfig;
  const setAppType = useElysiaStore.getState().setAppType;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const setAppFileBadgeTitle = useElysiaStore.getState().setAppFileBadgeTitle;
  const setIsAppFileAttached = useElysiaStore.getState().setIsAppFileAttached;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setSources = useElysiaStore.getState().setSources;
  const setContext = useElysiaStore.getState().setContext;
  const setLocalToolResultKey = useElysiaStore.getState().setLocalToolResultKey;
  const setLocalAutoScrollEnabledKey = useElysiaStore.getState().setLocalAutoScrollEnabledKey;
  const setServiceNowLink = useElysiaStore.getState().setServiceNowLink;

  const { profileInfo } = useContext(AuthContext);
  const [prompt, setPrompt] = useState<string>('');
  const [isProcessed, setProcessed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [appKey, setAppKey] = useState('');
  const [isProfileIncomplete, setIsProfileIncomplete] = useState<boolean>(false);
  const [careerCoachBlock, setCareerCoachBlock] = useState<string>('');
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [pendingModelName, setPendingModelName] = useState<string | null>(null);
  const { setSelectedLanguage, setSelectedModel, defaultLanguage, defaultModel, allModels } =
    useContext(SettingContext);

  lastScrollTop = 0;
  const promptContext: PromptContextStore = {
    prompt,
    isProcessed,
    setPrompt,
    setProcessed,
  };

  const loadingContext: LoadingContextStore = {
    isLoading,
    setIsLoading,
  };

  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const agentIdRef = useRef(params.get('app') || '');
  const fileUrlRef = useRef(params.get('file') || '');

  useEffect(() => {
    if (isAppTerminated === true && aiAppId === '') {
      agentIdRef.current = '';
    }
  }, [isAppTerminated, aiAppId]);

  useEffect(() => {
    if (fileUrlRef.current) {
      let filePath = fileUrlRef.current;
      if (/\.xlsx$/i.test(filePath)) {
        filePath = filePath.replace(/\.xlsx$/i, '_da.xlsx');
      } else if (/\.csv$/i.test(filePath)) {
        filePath = filePath.replace(/\.csv$/i, '_da.csv');
      }
      if (/\.mp4$/i.test(filePath)) {
        filePath = filePath.replace(/\.mp4$/i, '_video.txt');
      } else if (/\.mp3$/i.test(filePath)) {
        filePath = filePath.replace(/\.mp3$/i, '_audio.txt');
      } else if (/\.wav$/i.test(filePath)) {
        filePath = filePath.replace(/\.wav$/i, '_wav.txt');
      } else if (/\.m4a$/i.test(filePath)) {
        filePath = filePath.replace(/\.m4a$/i, '_m4a.txt');
      }
      setSelectedSources?.([filePath]);
      setSources?.([filePath]);
      setContext?.('myContentsOnly');
      setBrandDoc(true);
      setAppFileBadgeTitle(fileUrlRef.current);
      setIsAppFileAttached(true);
    }
    //eslint-disable-next-line
  }, [fileUrlRef.current]);

  useEffect(() => {
    if (agentIdRef.current && !isPrivateChat) {
      setAiAppId(agentIdRef.current);
    }
    //eslint-disable-next-line
  }, [agentIdRef.current, isPrivateChat]);

  // preload the components on landing page so they are ready to be used when needed.
  useEffect(() => {
    import('../components/chat/CodeBlock');
    import('../components/chat/TableCell');
    import('../components/home/ChatWithDoc');

    // Slight delay for other routes
    setTimeout(() => {
      import('../pages/MyProfilePage');
      import('../pages/ManageContentPage');
    }, 1000);
  }, []);

  useEffect(() => {
    if (aiAppId) {
      getAppDetails();
      setChatAgent?.('app-agent');
    }
    //eslint-disable-next-line
  }, [aiAppId]);

  useEffect(() => {
    if (pendingModelName && allModels?.length) {
      const appModal = allModels.find((model) => model?.name_of_model === pendingModelName);
      if (appModal) setSelectedModel(appModal);
      setPendingModelName(null);
    }
    //eslint-disable-next-line
  }, [pendingModelName, allModels, defaultModel]);

  useEffect(() => {
    const fetchIcon = async () => {
      const url = await getAppIcon(uploadedIcon?.iconId);
      setUploadedIcon((prev) => ({ ...prev, iconUrl: url }));
    };
    if (uploadedIcon?.iconId) fetchIcon();
    //eslint-disable-next-line
  }, [uploadedIcon?.iconId]);

  useEffect(() => {
    if (window.location.pathname !== '/sandbox') {
      const containerElt = Array.from(
        document.getElementsByClassName('main-container') as HTMLCollectionOf<HTMLElement>
      );
      containerElt.forEach((box) => {
        box.style.overflowY = 'auto';
        box.style.marginTop = '20px';
      });
    }
    setLocalStorageKeyNames();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (window.location.pathname === '/') {
      setSelectedLanguage(defaultLanguage);
      if (chatAgent !== 'app-agent') setSelectedModel(defaultModel);
    }
    // eslint-disable-next-line
  }, [window.location.pathname]);

  useEffect(() => {
    if (profileInfo) {
      const isProfileComplete =
        profileInfo.about && profileInfo.skills?.length && profileInfo.interests?.length;
      setIsProfileIncomplete(!isProfileComplete);
      if (!isProfileComplete && !(window.innerWidth < 768)) {
        const message =
          'Your user profile is incomplete, please select <a data-testid="profile-link" href="/myprofile">profile</a> to update your details';
        const type = 'error';
        notify({
          message,
          type,
          toastId: PROFILE_INCOMPLETE_TOAST_ID,
          autoClose: false,
        });
      }
    }
  }, [profileInfo]);

  useEffect(() => {
    if (appKey === 'career_coach' && isProfileIncomplete) {
      setShowBlockModal(true);
    } else {
      setShowBlockModal(false);
    }
  }, [isProfileIncomplete, appKey, careerCoachBlock]);

  useEffect(() => {
    const mainContentRef = contentRef.current;
    const handleStreamingScroll = () => {
      const currentScroll = mainContentRef.scrollTop;
      if (currentScroll + mainContentRef.offsetHeight + 2 > mainContentRef.scrollHeight) {
        sessionStorage.setItem(localAutoScrollEnabledKey, 'true');
        setShowScrollIcon?.(false);
      } else if (currentScroll + 2 < lastScrollTop && isChatActive === true) {
        sessionStorage.setItem(localAutoScrollEnabledKey, 'false');
        setShowScrollIcon?.(true);
      }
      lastScrollTop = currentScroll;
    };
    mainContentRef?.addEventListener('scroll', handleStreamingScroll);
    window.addEventListener('resize', handleStreamingScroll);
    return () => {
      sessionStorage.setItem(localAutoScrollEnabledKey, 'true');
      mainContentRef?.removeEventListener('scroll', handleStreamingScroll);
      window.removeEventListener('resize', handleStreamingScroll);
    };
    // eslint-disable-next-line
  }, [contentRef, isChatActive]);

  useEffect(() => {
    const mainContentRef = contentRef.current;
    if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
      checkScrollPosition();
    }
    if (
      mainContentRef.scrollHeight > mainContentRef.offsetHeight &&
      mainContentRef.scrollTop === 0
    ) {
      setShowScrollIcon?.(true);
    }
    if (isChatActive === false) {
      setShowScrollIcon?.(false);
    }
    // eslint-disable-next-line
  }, [dynamicStreamCount, isChatActive]);

  useEffect(() => {
    setLocalStorageKeyNames();
    // eslint-disable-next-line
  }, [chatSessionId]);

  const handleScrollToBottom = () => {
    contentRef.current.scrollTop = contentRef.current.scrollHeight;
  };

  const setLocalStorageKeyNames = () => {
    const suffix = chatSessionId?.slice(0, 6);
    setLocalAutoScrollEnabledKey?.(`autoScrollEnabled`); // session
    setLocalToolResultKey?.(`toolResult_${suffix}`);
  };

  const checkScrollPosition = () => {
    const parentDiv = document.getElementById('main-content');
    const targetClassName = 'user-msg-body';
    const targetElements = parentDiv?.getElementsByClassName(targetClassName);

    if (targetElements?.length && targetElements?.length > 0) {
      const lastTargetChild = targetElements[targetElements.length - 1];

      if (isElementAtTop(parentDiv, lastTargetChild)) {
        sessionStorage.setItem(localAutoScrollEnabledKey, 'false');
        if (targetElements?.length && targetElements?.length > 1) {
          setShowScrollIcon?.(true);
        }
      }
    }
  };

  // Function to check if the target element is at the top of the parent
  const isElementAtTop = (parentElement, targetElement) => {
    const parentRect = parentElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();

    // Check if the top of the target is at or above the top of the parent
    if (targetRect.top - 20 <= parentRect.top && targetRect.top > 0) {
      // correction of scroll to fix last user query at the top in view
      parentElement.scrollTop -= parentRect.top - targetRect.top;
      return true;
    }
    return false;
  };

  const markDisabledChat = async () => {
    setIsChatDisabled?.(true);
    setChatAgent?.('app-agent');
  };

  const getAppDetails = async () => {
    try {
      const response = await getAiApp(aiAppId);
      if (response.agent_status === 'unpublished' || response.agent_status === 'read-only') {
        markDisabledChat();
        return;
      }
      setName(response?.label);
      setAppKey(response?.key);
      setAppType(response?.agent_type);
      setDescription(response?.description);
      setGreetings(response?.greetings);
      setStarterPrompts(response?.example_prompts);
      setUploadedIcon((prev) => ({ ...prev, iconId: response?.icon_id }));
      setAppCreator(response?.created_by);
      setPendingModelName(response?.llm_model);
      setCareerCoachBlock(response?.is_accessible?.block_status);
      setServiceNowLink(response?.feedback_link?.trim() || SERVICE_NOW_LINK);
      setPromptToolsConfig({
        privateChat: response?.prompt_tools_config?.private_chat ?? true,
        chatWithDocuments: response?.prompt_tools_config?.chat_with_documents ?? true,
        addSources: response?.prompt_tools_config?.add_sources ?? true,
        attachFiles: response?.prompt_tools_config?.attach_files ?? true,
        settings: response?.prompt_tools_config?.settings ?? true,
        changeModel: response?.prompt_tools_config?.change_model ?? true,
        changeLanguage: response?.prompt_tools_config?.change_language ?? true,
        tools: response?.prompt_tools_config?.tools ?? true,
        savedPrompts: response?.prompt_tools_config?.saved_prompts ?? true,
        promptImprover: response?.prompt_tools_config?.prompt_improver ?? true,
        brandAgent: response?.prompt_tools_config?.brand_agent ?? false,
        configImage: response?.prompt_tools_config?.config_image ?? false,
        configFinance: response?.prompt_tools_config?.config_finance ?? false,
      });
    } catch (error) {
      console.error('Failed to fetch app details:', error);
      navigate('/', { replace: true });
      setChatAgent?.('Generic');
    }
  };

  return (
    <>
      <div
        ref={contentRef}
        id="main-content"
        className="content-scrollable overflow-y-auto flex-grow-1 d-flex justify-content-center me-2 mt-4 chat-interface"
        style={{ height: 'calc(100vh - 0px)', overflow: 'auto' }}
      >
        <div
          className={`width-control  ${isChatActive ? '' : 'landing-view align-self-md-center'} ${chatAgent === 'app-agent' ? 'brand-agent-welcome' : ''}`}
        >
          {!isChatActive && isPrivateChat && (
            <Suspense fallback={<div>Loading...</div>}>
              <PrivateChat />
            </Suspense>
          )}
          {!isChatActive && !isPrivateChat && chatAgent !== 'app-agent' && <MainHeader />}
          <LoadingContext.Provider value={loadingContext}>
            <PromptContext.Provider value={promptContext}>
              {!isChatActive && !isPrivateChat && chatAgent === 'app-agent' && <AppAgentWelcome />}
              {!isChatActive && !isPrivateChat && <SamplePrompts />}
              <ChatSession contentRef={contentRef} />
            </PromptContext.Provider>
          </LoadingContext.Provider>
        </div>
      </div>
      {!isRestrictedChatSessionStatus && (
        <div className="content-fixed-bottom d-flex justify-content-center chat-interface">
          <div className="width-control-bottom position-relative">
            {showScrollIcon && (
              <Button
                aria-label="scroll"
                data-testid="scroll-to-bottom-button"
                className="scroll-icon-button"
                onClick={handleScrollToBottom}
              >
                <ArrowDownwardsIcon />
              </Button>
            )}
            <LoadingContext.Provider value={loadingContext}>
              <PromptContext.Provider value={promptContext}>
                <ChatInput />
              </PromptContext.Provider>
            </LoadingContext.Provider>
          </div>
        </div>
      )}
      {appKey === 'career_coach' && (
        <BlockModal
          show={showBlockModal}
          hide={() => setShowBlockModal(false)}
          type={careerCoachBlock}
        />
      )}
    </>
  );
};

export default Home;
